/**
 * Block Model for FreshCards
 * Rich block content model supporting all block types
 *
 * ── F 116 migration note (v1.10.2) ────────────────────────────────────────
 * `BlockType` was renamed to match WHAT IS ACTUALLY STORED.
 *
 * Before F 116 this union declared FCr-flavoured names (`bulleted-list`,
 * `numbered-list`, `todo`, `toggle`, `code`) while
 * `src/core/workspace/pageContentStorage.ts` had been storing BlockNote's
 * canonical names (`bulletListItem`, `checkListItem`, `codeBlock`, ...)
 * since the editor landed. The two vocabularies never intersected, and
 * BlockNote's `blocksToMarkdownLossy` throws on any type absent from its
 * schema — so nothing in the published `BlockType` could be serialised.
 * F 115 measured this: the old names ALL THROW.
 *
 * `BlockType` is now the BlockNote 0.47 vocabulary, so the type describes
 * real data and is serialisable. The old names are retained below as
 * `LegacyBlockType` plus a runtime alias map. They are NOT removed —
 * removal is a separate dispatch, once consumers have migrated.
 */

export interface Block {
  id: string;
  type: BlockType;
  /**
   * F 131 — was `BlockContent`. The declared `{ richText?: RichText[] }`
   * shape described nothing that was ever stored: `pageContentStorage`,
   * BlockNote, the editor, F 117's serialiser and F 129's parser all use the
   * BlockNote inline array below. Which shape a consumer received depended on
   * which factory created the block, so this is a **breaking** correction.
   *
   * Per-type scalars (`level`, `checked`, `language`, `url`, `name`) live in
   * `attributes` — the bag the factories already used for `level`.
   */
  content: InlineNode[];
  children: Block[];
  attributes: Record<string, unknown>;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Canonical block vocabulary — BlockNote 0.47 node names.
 * This is what `pageContentStorage` stores and what BlockNote round-trips.
 *
 * @deprecated F 223 — use the block-as-entity kinds in
 * `EntityKind` (`src/core/adapters/filesystem/types.ts`) for NEW content.
 * This union is KEPT, not deleted, and not aliased.
 *
 * WHY NOT AN ALIAS (O1). An alias requires the two unions to be identical.
 * Measured at v2.15.0, they are not:
 *
 *   BOTH (7)        column, divider, heading, image, paragraph, quote, table
 *   BlockType only (10)
 *                   bulletListItem, checkListItem, codeBlock, columnList, file,
 *                   numberedListItem, pageBreak, pageContent, tableOfContent,
 *                   toggleListItem
 *   entity kind only (10)
 *                   callout, card, code, cta, embed, list, list-item, section,
 *                   slide, slider
 *
 * The two are answering different questions. BlockType is BlockNote's EDITOR
 * vocabulary — the shapes a rich-text editor can round-trip. The entity kinds
 * are FCr's CONTENT vocabulary — a CTA is a content thing that happens to be
 * editable. An alias would have to either invent ten entity kinds the starter
 * set does not name, or drop ten BlockTypes the editor genuinely uses; both
 * are breaking in one direction or the other.
 *
 * So: deprecate, and let the two coexist. Anything that renders or stores
 * rich text keeps this union. Anything that addresses content in the graph
 * uses `EntityKind`. `readPageContent()` is the seam — it reads child entities
 * when they exist and falls back to this union's blocks when they do not.
 *
 * Retiring it is a follow-up, and only after BlockNote round-tripping has a
 * replacement that can carry all ten BlockType-only shapes.
 */
export type BlockType =
  | 'paragraph'
  | 'heading'
  | 'bulletListItem'
  | 'numberedListItem'
  | 'checkListItem'
  | 'toggleListItem'
  | 'codeBlock'
  | 'quote'
  | 'table'
  | 'image'
  | 'file'
  | 'divider'
  | 'pageBreak'
  | 'tableOfContent'
  | 'column'
  | 'columnList'
  | 'pageContent';

/**
 * @deprecated F 116 — superseded by {@link BlockType}. These names were
 * declared but never stored; BlockNote does not recognise them and
 * `blocksToMarkdownLossy` throws on all of them. Use the BlockNote
 * equivalent instead:
 *
 *   'bulleted-list'  -> 'bulletListItem'
 *   'numbered-list' -> 'numberedListItem'
 *   'todo'          -> 'checkListItem'
 *   'toggle'        -> 'toggleListItem'
 *   'code'          -> 'codeBlock'
 *   'callout'       -> no BlockNote equivalent (see CALL_OUT_NOTE)
 *
 * Retained so existing imports keep compiling. Removal is a separate dispatch.
 */
export type LegacyBlockType =
  | 'bulleted-list'
  | 'numbered-list'
  | 'todo'
  | 'toggle'
  | 'code'
  | 'callout';

/**
 * @deprecated F 116 — runtime alias map, legacy name -> BlockNote name.
 * Apply with {@link normaliseBlockType} before serialising.
 *
 * 'callout' maps to 'quote' as the closest available BlockNote node. If
 * you need true callout semantics (icon + colour + background), the
 * markdown primitive's Cut 2 should introduce a first-class callout node
 * rather than overloading quote.
 */
export const LEGACY_BLOCK_TYPE_ALIASES: Readonly<Record<LegacyBlockType, BlockType>> =
  Object.freeze({
    'bulleted-list': 'bulletListItem',
    'numbered-list': 'numberedListItem',
    'todo': 'checkListItem',
    'toggle': 'toggleListItem',
    'code': 'codeBlock',
    'callout': 'quote',
  } as const);

/**
 * Normalise a possibly-legacy block type to the canonical vocabulary.
 * Unknown values pass through as 'paragraph' rather than throwing — a
 * migration must never fail on one unrecognised block.
 */
export function normaliseBlockType(type: string): BlockType {
  if ((LEGACY_BLOCK_TYPE_ALIASES as Record<string, BlockType>)[type]) {
    return (LEGACY_BLOCK_TYPE_ALIASES as Record<string, BlockType>)[type];
  }
  const known: readonly string[] = [
    'paragraph', 'heading', 'bulletListItem', 'numberedListItem',
    'checkListItem', 'toggleListItem', 'codeBlock', 'quote', 'table',
    'image', 'file', 'divider', 'pageBreak', 'tableOfContent',
    'column', 'columnList', 'pageContent',
  ];
  return (known.includes(type) ? type : 'paragraph') as BlockType;
}

/**
 * F 131 — the inline shape that is actually stored, by `pageContentStorage`,
 * by BlockNote, and read by F 117's serialiser and F 129's parser.
 */
export interface InlineNode {
  type: 'text' | 'link';
  text: string;
  styles: {
    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
    strikethrough?: boolean;
    code?: boolean;
    color?: string;
  };
  href?: string;
}

/**
 * @deprecated F 131 — superseded by {@link InlineNode}. `Block.content` is
 * now `InlineNode[]`; per-type scalars moved to `Block.attributes`.
 *
 * Kept for ONE release so existing consumer imports still compile. It will be
 * removed in v3. `coerceInlineContent()` accepts this shape at read time.
 */
export interface BlockContent {
  richText?: RichText[];
  checked?: boolean;
  language?: string;
  url?: string;
  emoji?: string;
}

/**
 * @deprecated F 131 — superseded by {@link InlineNode}. Use
 * `coerceInlineContent()` to convert at a boundary.
 *
 * Kept for ONE release. Will be removed in v3.
 */
export interface RichText {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strikethrough?: boolean;
  code?: boolean;
  link?: string;
  color?: string;
}

/** F 131 — a block as it may exist in localStorage written by an older build. */
export type LegacyBlock = Omit<Block, 'content'> & {
  content: unknown;
};

const EMPTY_NODE: InlineNode = { type: 'text', text: '', styles: {} };

/**
 * F 131 read-time adapter.
 *
 * Per F 130 this is a READ-TIME adaptation, not a data migration: block
 * content is per-browser `localStorage` with no server-side copy, so nothing
 * needs rewriting and no stored document is at risk. This runs on the way in,
 * so an old `{ richText: [...] }` document reads correctly and is rewritten in
 * the new shape the next time it is saved.
 *
 * Accepts, and never throws:
 *  - `InlineNode[]`                      the current shape — normalised and returned
 *  - `{ richText: RichText[] }`          the F 130 declared shape — converted
 *  - `RichText[]`                        a bare richText array — converted
 *  - `string`                            a plain string — one text node
 *  - `null` / `undefined` / anything     an empty array
 */
export function coerceInlineContent(value: unknown): InlineNode[] {
  if (value == null) return [];

  // current shape
  if (Array.isArray(value)) {
    const out: InlineNode[] = [];
    for (const node of value) {
      if (node == null) continue;
      if (typeof node === 'string') {
        if (node.length > 0) out.push({ type: 'text', text: node, styles: {} });
        continue;
      }
      if (typeof node !== 'object') continue;
      const n = node as Record<string, unknown>;
      // Two mark layouts exist and BOTH must be read:
      //   InlineNode — marks under `node.styles`   (the current shape)
      //   RichText   — marks at `node.bold` etc.   (the pre-F 131 shape the
      //                 old factories wrote: `content.richText = [{ text }]`)
      // Reading only `.styles` silently dropped every mark on old data, which
      // is precisely the case this adapter exists to handle.
      const styles = (n.styles ?? {}) as Record<string, unknown>;
      const mark = (k: string): boolean | undefined => {
        const v = styles[k] ?? n[k];
        return v === true ? true : undefined;
      };
      const color = typeof styles.color === 'string' ? styles.color
        : (typeof n.color === 'string' ? n.color : undefined);
      const href = typeof n.href === 'string' ? n.href
        : (typeof n.link === 'string' ? n.link : undefined);
      out.push({
        type: href ? 'link' : 'text',
        text: typeof n.text === 'string' ? n.text : '',
        styles: {
          ...(mark('bold') ? { bold: true } : {}),
          ...(mark('italic') ? { italic: true } : {}),
          ...(mark('underline') ? { underline: true } : {}),
          ...(mark('strikethrough') ? { strikethrough: true } : {}),
          ...(mark('code') ? { code: true } : {}),
          ...(color ? { color } : {}),
        },
        ...(href ? { href } : {}),
      });
    }
    return out;
  }

  if (typeof value === 'string') {
    return value.length > 0 ? [{ type: 'text', text: value, styles: {} }] : [];
  }

  if (typeof value === 'object') {
    const legacy = value as { richText?: unknown; text?: unknown };
    if (Array.isArray(legacy.richText)) return coerceInlineContent(legacy.richText);
    if (typeof legacy.text === 'string') {
      return legacy.text.length > 0 ? [{ type: 'text', text: legacy.text, styles: {} }] : [];
    }
  }

  return [];
}

/** Coerce a `RichText`-shaped node into an {@link InlineNode}. */
export function richTextToInlineNode(rt: RichText): InlineNode {
  const href = typeof rt.link === 'string' ? rt.link : undefined;
  return {
    type: href ? 'link' : 'text',
    text: typeof rt.text === 'string' ? rt.text : '',
    styles: {
      ...(rt.bold ? { bold: true } : {}),
      ...(rt.italic ? { italic: true } : {}),
      ...(rt.underline ? { underline: true } : {}),
      ...(rt.strikethrough ? { strikethrough: true } : {}),
      ...(rt.code ? { code: true } : {}),
      ...(typeof rt.color === 'string' ? { color: rt.color } : {}),
    },
    ...(href ? { href } : {}),
  };
}

/**
 * F 131 read-time adapter, whole block. Also lifts the F 130-era
 * `{ richText }` scalars (`checked`, `language`, `url`) into `attributes`,
 * which is where they live now.
 */
export function coerceBlock(input: unknown): Block | null {
  if (input == null || typeof input !== 'object') return null;
  const b = input as Record<string, unknown>;
  const raw = b.content as Record<string, unknown> | undefined;

  const attributes: Record<string, unknown> = {
    ...(b.attributes && typeof b.attributes === 'object' ? b.attributes as Record<string, unknown> : {}),
  };
  // F 129's parser mirrored `props` onto `attributes`; honour that.
  if (b.props && typeof b.props === 'object') Object.assign(attributes, b.props);

  // legacy scalars that lived on the old content object
  for (const key of ['checked', 'language', 'url', 'name', 'emoji'] as const) {
    if (raw && raw[key] !== undefined && attributes[key] === undefined) {
      attributes[key] = raw[key];
    }
  }

  return {
    id: typeof b.id === 'string' ? b.id : '',
    type: (typeof b.type === 'string' ? b.type : 'paragraph') as BlockType,
    content: coerceInlineContent(b.content),
    children: Array.isArray(b.children) ? b.children.map(coerceBlock).filter((x): x is Block => x !== null) : [],
    attributes,
    createdAt: b.createdAt instanceof Date ? b.createdAt : new Date(),
    updatedAt: b.updatedAt instanceof Date ? b.updatedAt : new Date(),
  };
}

/** Convenience: the plain text of a block's inline content. */
export function blockText(block: Pick<Block, 'content'>): string {
  return (coerceInlineContent(block.content)).map((n) => n.text).join('');
}

/**
 * Creates a new block with default values
 */
export function createBlock(id: string, type: BlockType = 'paragraph'): Block {
  const now = new Date();
  return {
    id,
    type,
    content: [],
    children: [],
    attributes: {},
    createdAt: now,
    updatedAt: now,
  };
}

/**
 * Creates a paragraph block with rich text content
 */
export function createParagraphBlock(id: string, text: string = ''): Block {
  const block = createBlock(id, 'paragraph');
  // F 131: inline content is an InlineNode array; it was `.richText = [...]`.
  if (text) block.content = [{ type: 'text', text, styles: {} }];
  return block;
}

/**
 * Creates a heading block
 */
export function createHeadingBlock(id: string, text: string, level: 1 | 2 | 3 = 1): Block {
  const block = createBlock(id, 'heading');
  block.content = [{ type: 'text', text, styles: {} }];
  block.attributes.level = level;
  return block;
}

/**
 * Creates a check-list (todo) block.
 * F 116: emits the canonical 'checkListItem' name, not the deprecated 'todo'.
 */
export function createTodoBlock(id: string, text: string = '', checked: boolean = false): Block {
  const block = createBlock(id, 'checkListItem');
  block.content = [{ type: 'text', text, styles: {} }];
  // F 131: `checked` was on content; per-type scalars now live in attributes,
  // which is where F 117's serialiser and F 129's parser already read them.
  block.attributes.checked = checked;
  return block;
}

/**
 * Creates a code block.
 * F 116: emits the canonical 'codeBlock' name, not the deprecated 'code'.
 */
export function createCodeBlock(id: string, code: string = '', language: string = 'plaintext'): Block {
  const block = createBlock(id, 'codeBlock');
  block.content = [{ type: 'text', text: code, styles: {} }];
  // F 131: `language` was on content; now in attributes (see createTodoBlock).
  block.attributes.language = language;
  return block;
}

/**
 * Creates an image block
 */
export function createImageBlock(id: string, url: string): Block {
  const block = createBlock(id, 'image');
  // F 131: `url` was on content; per-type scalars now live in attributes,
  // which is where F 117's serialiser reads an image's url.
  block.attributes.url = url;
  return block;
}